import {Button, registerStyle, styleRule, StyleSheet, UI} from "../../stem-core/src/ui/UI";
import {Device} from "../../stem-core/src/base/Device";

import {BUTTON_TYPE} from "../Constants";
import {DEFAULT_TRANSITION} from "../../blink-sdk/Constants.js";


export class BlinkButtonStyle extends StyleSheet {
    common = {
        padding: 12,
        borderRadius: this.themeProps.MERCHANT_BUTTON_INPUT_TOAST_BORDER_RADIUS,
        outline: 0,
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_LARGE,
        fontWeight: this.themeProps.MERCHANT_FONT_WEIGHT_BOLD,
        border: "none",
        cursor: "pointer",
        transition: DEFAULT_TRANSITION,
        "-webkit-user-select": "none",
        userSelect: "none",
        display: "inline-block",
    };

    primaryDisabled = {
        cursor: "not-allowed",
        textShadow: this.themeProps.BUTTON_PRIMARY_DISABLED_TEXT_SHADOW,
        background: this.themeProps.BUTTON_PRIMARY_DISABLED_BACKGROUND,
        color: this.themeProps.BUTTON_PRIMARY_DISABLED_COLOR,
        pointerEvents: () => (Device.isMobileDevice() ? "none" : ""), // TODO @cleanup WTF is this?
    };

    @styleRule
    primaryButton = Object.assign({}, this.common, {
        boxShadow: this.themeProps.BUTTON_PRIMARY_SHADOW,
        background: this.themeProps.BUTTON_PRIMARY_BACKGROUND,
        textShadow: this.themeProps.BUTTON_PRIMARY_TEXT_SHADOW,
        color: this.themeProps.BUTTON_PRIMARY_COLOR,
        ":hover": {
            background: this.themeProps.BUTTON_PRIMARY_HOVER_BACKGROUND,
            boxShadow: this.themeProps.BUTTON_PRIMARY_HOVER_SHADOW,
        },
        ":disabled": this.primaryDisabled,
        ":hover:disabled": this.primaryDisabled,
    });

    secondaryDisabled = {
        cursor: "not-allowed",
        color: this.themeProps.BUTTON_PRIMARY_DISABLED_COLOR,
        pointerEvents: () => (Device.isMobileDevice() ? "none" : ""),
    };

    @styleRule
    secondaryButton = Object.assign({}, this.common, {
        background: this.themeProps.BUTTON_SECONDARY_BACKGROUND,
        color: this.themeProps.BUTTON_SECONDARY_COLOR,
        boxShadow: this.themeProps.BUTTON_SECONDARY_SHADOW,
        ":hover": {
            color: this.themeProps.BUTTON_SECONDARY_COLOR,
            boxShadow: this.themeProps.BUTTON_SECONDARY_HOVER_SHADOW,
        },
        ":disabled": this.secondaryDisabled,
        ":hover:disabled": this.secondaryDisabled,
    });

    Size() {}
    Level() {}
}

@registerStyle(BlinkButtonStyle)
export class BlinkButton extends Button {
    getDefaultOptions(options) {
        return {
            ...super.getDefaultOptions(options),
            type: BUTTON_TYPE.PRIMARY,
        };
    }

    extraNodeAttributes(attr) {
        if (this.options.type === BUTTON_TYPE.PRIMARY) {
            attr.addClass(this.styleSheet.primaryButton);
        } else {
            attr.addClass(this.styleSheet.secondaryButton);
        }
    }

    // TODO Fully correct, a manual call to disable would also clear the timeout
    tempDisable(durationMs) {
        this.disable();
        this.attachTimeout(() => this.enable(), durationMs);
    }
}

// TODO @mustfix deprecate???
export class BlinkLinkButton extends UI.Primitive(BlinkButton, "a") {}

export class ToggleButtonStyle extends StyleSheet {
    transitionTime = 0.18;

    @styleRule
    extraPadding = {
        padding: 4,
        margin: -4,
        cursor: "pointer",
        "[disabled]": {
            cursor: "not-allowed",
        },
    };

    @styleRule
    button = {
        width: 35,
        height: 15,
        position: "relative",
        background: this.themeProps.TOGGLE_BACKGROUND,
        borderRadius: 20,
        ">:first-child": {
            height: 20,
            width: 20,
            top: -3,
            borderRadius: "100%",
            position: "absolute",
            left: 0,
            background: this.themeProps.TOGGLE_DISABLED_BACKGROUND,
            boxShadow: this.themeProps.TOGGLE_SHADOW,
        },
    };

    @styleRule
    active = {
        background: this.themeProps.TOGGLE_BACKGROUND,
        ">:first-child": {
            left: 15,
            background: this.themeProps.TOGGLE_COLOR,
        },
    };

    @styleRule
    transition = {
        pointerEvents: "none",
        cursor: "initial",
        ">:first-child": {
            transition: "background " + this.transitionTime + "s ease",
            ">:first-child": {
                transitionProperty: "background, left",
                transition: this.transitionTime + "s ease",
            },
        }
    };
}

@registerStyle(ToggleButtonStyle)
export class ToggleButton extends UI.Element {
    getDefaultOptions() {
        return {
            active: false,
            autoChange: true,
            onChange: () => {},
        };
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.addClass(this.styleSheet.extraPadding);
        if (this.useTransition) {
            attr.addClass(this.styleSheet.transition);
        }
    }

    render() {
        const {styleSheet} = this;

        return <div className={styleSheet.button + (this.options.active ? styleSheet.active : "")} ref="button">
            <div/>
            {super.render()}
        </div>;
    }

    toggleActive(autoChange) {
        if (this.options.disabled) {
            return;
        }
        const {styleSheet} = this;
        this.useTransition = true;
        this.redraw();
        this.dispatch("toggle", {active: !this.options.active});

        // TODO: merge with the previous dispatch in a single method
        if (this.options.onChange) {
            this.options.onChange(!this.options.active, this);
        }

        if (autoChange) {
            this.updateOptions({active: !this.options.active});
        }
        setTimeout(() => {
            this.useTransition = false;
            this.redraw();
        }, styleSheet.transitionTime * 1000);
    }

    onMount() {
        super.onMount();
        this.addClickListener(() => this.toggleActive(this.options.autoChange));
    }
}

export class ActiveButtonStyle extends StyleSheet {
    @styleRule
    button = {
        fontSize: this.themeProps.ACTIVE_BUTTON_FONT_SIZE,
        textAlign: "center",
        color: this.themeProps.ACTIVE_BUTTON_INACTIVE_COLOR,
        background: this.themeProps.ACTIVE_BUTTON_INACTIVE_BACKGROUND,
        border: this.themeProps.ACTIVE_BUTTON_INACTIVE_BORDER,
        borderRadius: 0,
        userSelect: "none",
        "-webkit-user-select": "none",
        outline: 0,
        cursor: "pointer",
        transition: DEFAULT_TRANSITION,
        ":hover": {
            background: this.themeProps.ACTIVE_BUTTON_INACTIVE_HOVER_BACKGROUND,
        },
    };

    @styleRule
    active = {
        color: this.themeProps.ACTIVE_BUTTON_COLOR,
        background: this.themeProps.ACTIVE_BUTTON_BACKGROUND,
        border: this.themeProps.ACTIVE_BUTTON_ACTIVE_BORDER,
        ":hover": {
            background: this.themeProps.ACTIVE_BUTTON_BACKGROUND,
        },
    };
}

@registerStyle(ActiveButtonStyle)
export class ActiveButton extends UI.Primitive("button") {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            label: "",
            active: false,
        }
    }

    getLabel() {
        return this.options.label;
    }

    getChildrenToRender() {
        return [this.getLabel(), this.render()];
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.addClass(this.styleSheet.button);
        if (this.options.active) {
            attr.addClass(this.styleSheet.active);
        }
    }
}

